<template>
	<ion-modal :is-open="isOpen" @ion-modal-will-dismiss="closeModal">
		<ion-header>
			<ion-toolbar color="branding">
				<ion-title class="text-lg font-medium">
					{{ $t("user-modal.edit-roles") }}
				</ion-title>
				<ion-buttons slot="end">
					<ion-button @click="closeModal">
						<ion-icon :icon="closeOutline" />
					</ion-button>
				</ion-buttons>
			</ion-toolbar>
		</ion-header>
		<ion-content class="ion-padding">
			<RequirePermission :permissions="[PermissionKey.ManageUserRoles]">
				<template v-if="!isSubmitting">
					<EditUserRoleList
						v-model:active-roles="activeRoles"
						class="rounded-lg bg-gray-100 p-2 pl-3"
						:show-internal="user.internalUser"
					/>
					<div v-if="isError" class="py-2 font-bold text-red-500">
						{{ $t("user-management.roles-update-error") }}
					</div>
				</template>
				<template v-else>
					<GenericLoader
						:message="$t('user-management.submitting-changes')"
					/>
				</template>
			</RequirePermission>
		</ion-content>

		<ion-footer v-if="!isSubmitting">
			<ion-toolbar>
				<div class="flex flex-row gap-1">
					<ion-button
						color="medium"
						class="flex-1"
						@click="closeModal"
					>
						{{ $t("common.cancel") }}
					</ion-button>
					<ion-button
						v-if="changesMade"
						class="flex-1"
						@click="submit"
					>
						{{ $t("common.save-changes") }}
					</ion-button>
				</div>
			</ion-toolbar>
		</ion-footer>
	</ion-modal>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import { captureException } from "@sentry/vue";
	import {
		IonButton,
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonButtons,
		IonIcon,
		IonContent,
		IonFooter,
	} from "@ionic/vue";
	import { closeOutline } from "ionicons/icons";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import { useSetUserRoles } from "seedgreen-shared/queries/user";
	import { sendToast } from "@/utils/sendToast";
	import RequirePermission from "@/components/feature/auth/RequirePermission.vue";
	import GenericLoader from "@/components/ui/GenericLoader.vue";
	import EditUserRoleList from "@/components/feature/user/role/EditUserRoleList.vue";
	import { useI18n } from "vue-i18n";
	import _ from "lodash";

	const props = defineProps<{
		user: GVAUserPrivilegedDTO;
	}>();

	const { t } = useI18n();

	const activeRoles = ref<number[]>([]);
	const isSubmitting = ref(false);
	const isOpen = ref(false);

	const { setUserRoles, isError } = useSetUserRoles();

	watch(
		() => props.user,
		(user) => {
			activeRoles.value = [...(user.roleIds || [])];
		},
		{ immediate: true },
	);

	const changesMade = computed(() => {
		const initialRoles = props.user.roleIds || [];
		const newRoles = activeRoles.value || [];
		return !_.isEqual(_.sortBy(initialRoles), _.sortBy(newRoles));
	});

	async function submit() {
		isSubmitting.value = true;
		try {
			await setUserRoles({
				userId: props.user.id,
				roles: activeRoles.value,
			});
			sendToast(t("user-management.submitting-changes"), 1000, "success");
			closeModal();
		} catch (error) {
			console.error(error);
			captureException(error);
		} finally {
			isSubmitting.value = false;
		}
	}

	const openModal = () => {
		isOpen.value = true;
	};

	const closeModal = async () => {
		isOpen.value = false;
	};

	defineExpose({ openModal, closeModal });
</script>
